/**
 * Tiny hash router for the Home: `#/projects`, `#/games`, `#/quizzes`,
 * `#/shared`, `#/demos`, `#/bin` (recently deleted), `#/folder/<id>`, `#/project/<id>`, `#/classroom/<id>`,
 * `#/account`, `#/subscription`, `#/school` (a school licence's teachers), `#/settings`, `#/help`, `#/student` (a classroom student, no account; `#/student/<code>` fills in the classroom code),
 * `#/edit/<id>` (the project editor, full screen).
 * The Player keeps using the query string (`?project=`, `?code=`, `?demo=`).
 */
import { useEffect, useState } from "react";

export type Route =
  | { name: "projects"; filter: "all" | "game" | "quiz" }
  | { name: "shared" }
  | { name: "demos" }
  | { name: "bin" }
  | { name: "folder"; id: number }
  | { name: "project"; id: string }
  | { name: "account" }
  | { name: "settings" }
  | { name: "help" }
  | { name: "subscription" }
  | { name: "school" }
  | { name: "classroom"; id: string }
  | { name: "edit"; id: string }
  | { name: "student"; code?: string };

export function parseRoute(hash: string = window.location.hash): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  switch (parts[0]) {
    case "games":
      return { name: "projects", filter: "game" };
    case "quizzes":
      return { name: "projects", filter: "quiz" };
    case "shared":
      return { name: "shared" };
    case "demos":
      return { name: "demos" };
    case "bin":
      return { name: "bin" };
    case "folder":
      return { name: "folder", id: Number(parts[1] ?? 0) };
    case "project":
      return parts[1] ? { name: "project", id: parts[1] } : { name: "projects", filter: "all" };
    case "account":
      return { name: "account" };
    case "settings":
      return { name: "settings" };
    case "help":
      return { name: "help" };
    case "subscription":
      return { name: "subscription" };
    case "school":
      return { name: "school" };
    case "classroom":
      return parts[1] ? { name: "classroom", id: parts[1] } : { name: "projects", filter: "all" };
    case "student":
      return parts[1] ? { name: "student", code: decodeURIComponent(parts[1]) } : { name: "student" };
    case "edit":
      return parts[1] ? { name: "edit", id: parts[1] } : { name: "projects", filter: "all" };
    default:
      return { name: "projects", filter: "all" };
  }
}

export function hrefOf(route: Route): string {
  switch (route.name) {
    case "projects":
      return route.filter === "game" ? "#/games" : route.filter === "quiz" ? "#/quizzes" : "#/projects";
    case "folder":
      return `#/folder/${route.id}`;
    case "project":
      return `#/project/${route.id}`;
    case "classroom":
      return `#/classroom/${route.id}`;
    case "edit":
      return `#/edit/${route.id}`;
    case "student":
      return route.code ? `#/student/${encodeURIComponent(route.code)}` : "#/student";
    default:
      return `#/${route.name}`;
  }
}

export function navigate(route: Route) {
  window.location.hash = hrefOf(route);
}

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute());
  useEffect(() => {
    const on = () => setRoute(parseRoute());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}

export function sameRoute(a: Route, b: Route): boolean {
  return hrefOf(a) === hrefOf(b);
}
